"use client";

import { useEffect, useId, useRef } from "react";
import { cn } from "@/lib/utils";

const COPIES = 4;

/**
 * Text that drifts along a wavy line. Each copy of the phrases is measured, and the
 * offset loops by exactly one copy's length so the seam never shows.
 */
export default function FlowingText({
  items,
  path = "M-40 130 C 260 40, 520 40, 760 115 S 1240 200, 1480 95",
  fontSize = 26,
  speed = 40,
  showLine = true,
  className,
  textClassName,
}) {
  const id = useId().replace(/:/g, "");
  const svgRef = useRef(null);
  const textPathRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const unit = textRef.current.getComputedTextLength() / COPIES;
    let offset = 0;
    let last = performance.now();
    let frame = 0;
    let visible = false;

    const tick = (now) => {
      offset = (offset + ((now - last) / 1000) * speed) % unit;
      last = now;
      textPathRef.current.setAttribute("startOffset", String(-offset));
      frame = requestAnimationFrame(tick);
    };

    // Only animate while on screen.
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !visible) {
        visible = true;
        last = performance.now();
        frame = requestAnimationFrame(tick);
      } else if (!entry.isIntersecting && visible) {
        visible = false;
        cancelAnimationFrame(frame);
      }
    });
    io.observe(svgRef.current);
    return () => {
      io.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [speed]);

  return (
    <svg ref={svgRef} viewBox="0 0 1440 220" className={cn("w-full overflow-visible", className)} aria-hidden="true">
      <path id={id} d={path} fill="none" stroke={showLine ? "currentColor" : "none"} strokeOpacity="0.18" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
      <text ref={textRef} dy="-12" fontSize={fontSize} className={cn("fill-current whitespace-pre", textClassName)}>
        <textPath ref={textPathRef} href={`#${id}`}>
          {Array.from({ length: COPIES }, (_, c) =>
            items.map((item, i) => (
              <tspan key={`${c}-${i}`}>
                {item}
                <tspan className="fill-cta">{"  •  "}</tspan>
              </tspan>
            ))
          )}
        </textPath>
      </text>
    </svg>
  );
}
